import { Button, Checkbox, Col, DatePicker, Form, Input, Radio, Select, Space } from 'antd'
import type { FormInstance } from 'antd/es/form'
import dayjs, { type Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'
import type { FC, ReactNode } from 'react'
dayjs.extend(utc)
dayjs.extend(duration)
const defaultDateFormat = 'DD-MM-YYYY'

export type OptionSelect = {
    label: any
    value: string | number | boolean
}
type BaseElementPorps = {
    name: string
    label?: string | ReactNode
    rules?: any
    placeHolder?: string
    disable?: boolean
    form?: FormInstance
}

type InputUiProps = BaseElementPorps & {
    onChangeInput?: (e: any) => void
    prefix?: any
}

export const InputUi: FC<InputUiProps> = ({
    name,
    label,
    rules,
    placeHolder,
    disable,
    onChangeInput,
    prefix,
    form,
}) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (form) {
            form.setFieldValue(name, e.target.value)
        }
        if (onChangeInput) {
            onChangeInput(e)
        }
    }

    return (
        <Form.Item
            className='m0 mt08'
            label={label && <label style={{ color: '#E36429' }}>{label}</label>}
            name={name}
            rules={rules ? rules : []}
        >
            <Input
                autoComplete='off'
                size='large'
                placeholder={placeHolder}
                disabled={disable}
                onInput={handleInputChange}
                prefix={prefix ?? undefined}
            />
        </Form.Item>
    )
}

type SelectUiProps = BaseElementPorps & {
    onChange?: (e: any) => void
    options: { label: string; value: any }[]
}
export const SelectUi: FC<SelectUiProps> = ({
    name,
    label,
    rules,
    placeHolder,
    disable,
    onChange,
    options,
}) => (
    <Form.Item
        className='m0 mt08'
        label={label && <label style={{ color: '#E36429' }}>{label}</label>}
        name={name}
        rules={rules ? rules : []}
    >
        <Select
            size='large'
            placeholder={placeHolder}
            onChange={onChange}
            options={options}
            disabled={disable}
        />
    </Form.Item>
)

type InputButtonUiProps = BaseElementPorps & {
    buttonLabel?: string | ReactNode
    btnDisable?: boolean
    onClickButton?: () => void
}
export const InputButtonUi: FC<InputButtonUiProps> = ({
    name,
    label,
    rules,
    placeHolder,
    buttonLabel,
    disable,
    onClickButton,
    btnDisable,
}) => (
    <Form.Item className='m0 mt08' label={label} name={name} rules={rules ? rules : []}>
        <Space.Compact style={{ width: '100%' }}>
            <Input size='large' placeholder={placeHolder} disabled={disable} />
            <Button size='large' type='primary' onClick={onClickButton} disabled={btnDisable}>
                {buttonLabel}
            </Button>
        </Space.Compact>
    </Form.Item>
)

type RaduiButtonUiProps = BaseElementPorps & {
    options: { label: string; value: any }[]
}
export const RadioButtonUi: FC<RaduiButtonUiProps> = ({ name, label, rules, options, disable }) => (
    <Form.Item className='m0 mt08' label={label} name={name} rules={rules ? rules : []}>
        <Radio.Group disabled={disable}>
            {options?.map(e => (
                <Col key={e.label} span={24}>
                    <Radio value={e.value}>{e.label}</Radio>
                </Col>
            ))}
        </Radio.Group>
    </Form.Item>
)

type DatePickerUiProps = {
    formatDate?: string
    minCurrentDif?: [number, dayjs.ManipulateType]
    maxCurrentDif?: [number, dayjs.ManipulateType]
    defaultPickerValue?: Dayjs
} & BaseElementPorps

export const DatePickerUi: FC<DatePickerUiProps> = ({
    name,
    label,
    rules,
    placeHolder,
    formatDate = defaultDateFormat,
    minCurrentDif,
    maxCurrentDif,
    defaultPickerValue,
}) => {
    const disabledDateFunc = (current: Dayjs) => {
        if (!current) return false
        if (
            minCurrentDif &&
            typeof minCurrentDif[0] === 'number' &&
            current < dayjs().subtract(minCurrentDif[0], minCurrentDif[1])
        )
            return true
        if (
            maxCurrentDif &&
            typeof maxCurrentDif[0] === 'number' &&
            current > dayjs().add(maxCurrentDif[0], maxCurrentDif[1])
        )
            return true
        return false
    }
    return (
        <Form.Item className='m0 mt08' label={label} name={name} rules={rules ? rules : []}>
            <DatePicker
                className='wfull'
                size='large'
                placeholder={placeHolder}
                allowClear
                placement='bottomRight'
                format={formatDate}
                defaultPickerValue={defaultPickerValue}
                disabledDate={disabledDateFunc}
            />
        </Form.Item>
    )
}

export const CheckBoxUi: FC<{
    name: string
    label?: string | ReactNode
    rules?: any
    disable?: boolean
    options: OptionSelect[]
    onChange?: (e: any) => void
}> = ({ name, label, rules, options, disable, onChange }) => (
    <Form.Item
        className='m0 mt08'
        valuePropName='checked'
        label={label}
        name={name}
        rules={rules ? rules : []}
    >
        <Checkbox style={{ margin: '0' }} disabled={disable} onChange={onChange}>
            {options[0].label}
        </Checkbox>
    </Form.Item>
)
export const CheckBoxGroupUi: FC<{
    name: string
    label?: string | ReactNode
    rules?: any
    disable?: boolean
    options: OptionSelect[]
    onChange?: (e: any) => void
}> = ({ name, label, rules, options, disable, onChange }) => (
    <Form.Item className='m0 mt08' label={label} name={name} rules={rules ? rules : []}>
        <Checkbox.Group style={{ margin: '0' }} disabled={disable} onChange={onChange}>
            {options?.map(e => (
                <Col key={e.label} span={24}>
                    <Checkbox value={e.value}>{e.label}</Checkbox>
                </Col>
            ))}
        </Checkbox.Group>
    </Form.Item>
)
